import "@/App.css";
import React, { useState, useRef, useEffect } from "react";
import { motion, useMotionValue, useTransform, AnimatePresence, useDragControls } from "framer-motion";
import { getAppScreen } from "@/iphone/Apps";
import OnePager from "@/OnePager";

const PASSCODE = "1234";

/* ----------------------------- App icon set ----------------------------- */
const RY_DRIVER_SRC = "/assets/ry-driver.png";
const RY_SHIP_SRC = "/assets/ry-ship.png";
const RY_COMMAND_SRC = "/assets/ry-command.png";

function RYIcon({ src }) {
  return (
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 16,
        overflow: "hidden",
        background: "#000",
        boxShadow:
          "0 2px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.18)",
      }}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </div>
  );
}

const RYDriver = () => <RYIcon src={RY_DRIVER_SRC} />;
const RYShip = () => <RYIcon src={RY_SHIP_SRC} />;
const RYCommand = () => <RYIcon src={RY_COMMAND_SRC} />;
const RYWeb = () => (
  <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, #ff6b00 0%, #b34800 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)", position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.25), transparent 50%)" }} />
    <svg width="34" height="26" viewBox="0 0 34 26" fill="none" style={{ position: "relative", zIndex: 1 }}>
      <rect x="1" y="1" width="32" height="20" rx="2.5" stroke="white" strokeWidth="1.6" fill="rgba(0,0,0,0.25)" />
      <rect x="3" y="3" width="28" height="16" rx="1.5" fill="rgba(255,255,255,0.12)" />
      <circle cx="4" cy="4.5" r="0.7" fill="white" />
      <circle cx="6" cy="4.5" r="0.7" fill="white" />
      <circle cx="8" cy="4.5" r="0.7" fill="white" />
      <rect x="10" y="22" width="14" height="2" rx="1" fill="white" />
      <text x="17" y="13.5" fontSize="6" fontWeight="900" textAnchor="middle" fill="white" fontFamily="-apple-system">RY</text>
    </svg>
  </div>
);

function Icon({ children, bg, radius = 16 }) {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        width: 56,
        height: 56,
        borderRadius: radius,
        background: bg,
        boxShadow:
          "0 1px 2px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.18)",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

const Messages = () => (
  <Icon bg="linear-gradient(180deg, #5ee666 0%, #1ea93a 100%)">
    <svg width="36" height="36" viewBox="0 0 36 36" fill="white">
      <path d="M18 7c-7 0-12 4.4-12 9.8 0 3 1.6 5.7 4.2 7.4-.3 1.3-1 2.8-2.1 3.7-.3.2-.2.6.2.6 2.4 0 4.4-.9 5.9-2 1.2.4 2.5.6 3.8.6 7 0 12-4.4 12-9.8C30 11.4 25 7 18 7z" />
    </svg>
  </Icon>
);

const Phone = () => (
  <Icon bg="linear-gradient(180deg, #6be079 0%, #0fa31a 100%)">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
      <path d="M19.4 14.2c-1.2-.1-2.3-.4-3.4-.7-.4-.1-.8 0-1 .2l-2 2c-2.6-1.3-4.7-3.5-6-6l2-2c.3-.3.3-.7.2-1-.4-1-.6-2.2-.7-3.4 0-.5-.5-.9-1-.9H4.2c-.5 0-1 .4-1 .9-.3 8.4 6.5 15.2 14.9 14.9.5 0 .9-.5.9-1v-3.1c0-.5-.4-.9-.6-.9z"/>
    </svg>
  </Icon>
);

const Mail = () => (
  <Icon bg="linear-gradient(180deg, #5a9dff 0%, #0a64dc 100%)">
    <svg width="32" height="22" viewBox="0 0 32 22" fill="none" stroke="white" strokeWidth="2.2" strokeLinejoin="round">
      <rect x="2" y="3" width="28" height="16" rx="2.5" />
      <path d="M3 5l13 9 13-9" />
    </svg>
  </Icon>
);

const Safari = () => (
  <Icon bg="radial-gradient(circle at 50% 50%, #f8f8f8 0%, #c8d4e0 100%)">
    <div className="relative" style={{ width: 46, height: 46 }}>
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, #1f8cff, #2aa3ff, #1576e3, #0e63c4, #1f8cff)",
          padding: 3,
        }}
      >
        <div className="w-full h-full rounded-full bg-white relative flex items-center justify-center">
          {/* tick marks */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-gray-400"
              style={{
                width: 1,
                height: 4,
                top: 2,
                left: "50%",
                transformOrigin: "0.5px 18px",
                transform: `rotate(${i * 30}deg)`,
              }}
            />
          ))}
          {/* needle */}
          <div
            className="absolute"
            style={{
              width: 4,
              height: 22,
              transform: "rotate(45deg)",
              top: 9,
              left: 18,
            }}
          >
            <div style={{ width: 0, height: 0, borderLeft: "2px solid transparent", borderRight: "2px solid transparent", borderBottom: "10px solid #e64545" }} />
            <div style={{ width: 0, height: 0, borderLeft: "2px solid transparent", borderRight: "2px solid transparent", borderTop: "10px solid white", marginTop: 0 }} />
          </div>
          <div className="absolute w-2 h-2 rounded-full bg-white border border-gray-300" />
        </div>
      </div>
    </div>
  </Icon>
);

const Maps = () => (
  <Icon bg="linear-gradient(180deg, #d2e6ff 0%, #b8d6f0 100%)" radius={16}>
    <div className="relative w-full h-full">
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #e8f0db 30%, #b8d6f0 30% 60%, #d4e8c8 60%)" }} />
      <div className="absolute" style={{ top: 8, left: 18, width: 20, height: 2, background: "white", transform: "rotate(40deg)", borderRadius: 2 }} />
      <div className="absolute" style={{ top: 28, left: 8, width: 30, height: 2, background: "white", transform: "rotate(-20deg)", borderRadius: 2 }} />
      <div className="absolute flex items-center justify-center" style={{ top: 14, left: 22 }}>
        <div style={{ width: 16, height: 22, position: "relative" }}>
          <div style={{ width: 16, height: 16, borderRadius: "50%", background: "linear-gradient(180deg, #ff5a5a, #d11)", boxShadow: "0 2px 4px rgba(0,0,0,0.3)" }} />
          <div style={{ position: "absolute", left: 5, top: 14, width: 6, height: 6, background: "#d11", clipPath: "polygon(50% 100%, 0 0, 100% 0)" }} />
        </div>
      </div>
    </div>
  </Icon>
);

const Photos = () => (
  <Icon bg="white">
    <div className="relative" style={{ width: 42, height: 42 }}>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const colors = ["#ff3b30", "#ff9500", "#ffcc00", "#34c759", "#5ac8fa", "#007aff", "#af52de", "#ff2d55"];
        return (
          <div
            key={i}
            className="absolute"
            style={{
              top: "50%",
              left: "50%",
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: colors[i],
              transform: `translate(-50%, -50%) rotate(${i * 45}deg) translate(0, -10px)`,
              mixBlendMode: "multiply",
              opacity: 0.9,
            }}
          />
        );
      })}
    </div>
  </Icon>
);

const Camera = () => (
  <Icon bg="linear-gradient(180deg, #4a4a4c 0%, #1c1c1e 100%)">
    <svg width="34" height="30" viewBox="0 0 34 30" fill="white">
      <path d="M11 4l-2 3H4a2 2 0 00-2 2v15a2 2 0 002 2h26a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-3H11z" />
      <circle cx="17" cy="17" r="6" fill="#1c1c1e" />
      <circle cx="17" cy="17" r="4.5" fill="none" stroke="white" strokeWidth="1.2" />
    </svg>
  </Icon>
);

const Notes = () => (
  <Icon bg="linear-gradient(180deg, #ffffff 0%, #ffe96a 30%, #ffd400 100%)">
    <div style={{ width: 38, height: 38, background: "white", borderRadius: 4, padding: 6, display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ height: 2, background: "#e0c000", borderRadius: 1 }} />
      <div style={{ height: 2, background: "#e0c000", borderRadius: 1 }} />
      <div style={{ height: 2, background: "#e0c000", borderRadius: 1, width: "60%" }} />
    </div>
  </Icon>
);

const Calendar = () => (
  <Icon bg="white">
    <div style={{ width: "100%", height: "100%", padding: 4, display: "flex", flexDirection: "column" }}>
      <div style={{ fontSize: 9, color: "#ff3b30", fontWeight: 600, textAlign: "center", fontFamily: "-apple-system" }}>FRIDAY</div>
      <div style={{ fontSize: 26, color: "#1c1c1e", fontWeight: 300, textAlign: "center", lineHeight: 1, marginTop: -2, fontFamily: "-apple-system" }}>20</div>
    </div>
  </Icon>
);

const Clock = () => (
  <Icon bg="#0a0a0a">
    <div className="relative" style={{ width: 46, height: 46 }}>
      <div className="absolute inset-0 rounded-full border-2 border-white" />
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="absolute bg-white"
          style={{
            width: 2,
            height: 4,
            top: 2,
            left: "50%",
            transformOrigin: "1px 21px",
            transform: `translateX(-50%) rotate(${i * 90}deg)`,
          }}
        />
      ))}
      <div className="absolute" style={{ width: 2, height: 16, background: "white", left: "50%", top: "50%", transformOrigin: "1px 1px", transform: "translate(-50%, 0) rotate(-30deg)" }} />
      <div className="absolute" style={{ width: 1.5, height: 20, background: "#ff9500", left: "50%", top: "50%", transformOrigin: "0.75px 0.75px", transform: "translate(-50%, 0) rotate(120deg)" }} />
      <div className="absolute" style={{ width: 6, height: 6, borderRadius: "50%", background: "#ff9500", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
    </div>
  </Icon>
);

const Weather = () => (
  <Icon bg="linear-gradient(180deg, #4ba6ff 0%, #0066ff 100%)">
    <svg width="38" height="30" viewBox="0 0 38 30" fill="white">
      <circle cx="14" cy="11" r="6" fill="#ffd400" />
      <path d="M26 13a6 6 0 00-11.7-1.7A5 5 0 0010 20h17a4.5 4.5 0 00-1-7z" />
    </svg>
  </Icon>
);

const Settings = () => (
  <Icon bg="linear-gradient(180deg, #c8c8cc 0%, #5a5a5e 100%)">
    <svg width="38" height="38" viewBox="0 0 38 38" fill="#2a2a2c">
      <path d="M19 5l2 3 3.5-1 1 3.5L29 12l-1 3.5 3 2-3 2 1 3.5-3.5.5-1 3.5L21 25l-2 3-2-3-3.5 1-1-3.5L9 22l1-3.5-3-2 3-2L9 11l3.5-.5L13.5 7 17 8l2-3z" />
      <circle cx="19" cy="17" r="5" fill="#c8c8cc" />
    </svg>
  </Icon>
);

const AppStore = () => (
  <Icon bg="linear-gradient(180deg, #1ca8f5 0%, #006edc 100%)">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 22l6-12 6 12M11 19h8" />
    </svg>
  </Icon>
);

const Music = () => (
  <Icon bg="linear-gradient(180deg, #fc4477 0%, #f6376c 50%, #ff5e62 100%)">
    <svg width="30" height="30" viewBox="0 0 30 30" fill="white">
      <path d="M22 4l-12 2.5v14a4 4 0 11-2-3.5V8l10-2v9a4 4 0 11-2-3.5V4z" />
    </svg>
  </Icon>
);

const Calculator = () => (
  <Icon bg="#1c1c1e">
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, padding: 6 }}>
      {[...Array(9)].map((_, i) => (
        <div
          key={i}
          style={{
            width: 11,
            height: 8,
            borderRadius: 4,
            background: i === 2 || i === 5 || i === 8 ? "#ff9500" : "#505052",
          }}
        />
      ))}
    </div>
  </Icon>
);

const FaceTime = () => (
  <Icon bg="linear-gradient(180deg, #6be079 0%, #0fa31a 100%)">
    <svg width="32" height="22" viewBox="0 0 32 22" fill="white">
      <rect x="2" y="3" width="22" height="16" rx="3" />
      <path d="M24 9l6-4v12l-6-4z" />
    </svg>
  </Icon>
);

const Wallet = () => (
  <Icon bg="#1c1c1e">
    <svg width="34" height="26" viewBox="0 0 34 26" fill="none">
      <rect x="2" y="6" width="30" height="18" rx="3" fill="#e64545" />
      <rect x="2" y="3" width="30" height="18" rx="3" fill="#ff9500" />
      <rect x="2" y="0" width="30" height="18" rx="3" fill="#1ca8f5" />
    </svg>
  </Icon>
);

const Notes2 = Notes;

/* ----------------------------- Phone Frame ----------------------------- */
function PhoneFrame({ children }) {
  return (
    <div
      data-testid="phone-mockup"
      className="relative"
      style={{ width: 380, height: 780 }}
    >
      {/* titanium frame */}
      <div
        className="absolute inset-0 rounded-[58px]"
        style={{
          background:
            "linear-gradient(135deg, #3a3a3c 0%, #1c1c1e 25%, #2c2c2e 50%, #0c0c0e 75%, #1c1c1e 100%)",
          boxShadow:
            "0 60px 120px -20px rgba(0,0,0,0.7), 0 30px 60px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.6)",
        }}
      />
      <div
        className="absolute rounded-[52px]"
        style={{
          inset: 4,
          background:
            "linear-gradient(135deg, #0a0a0a 0%, #1a1a1c 50%, #0a0a0a 100%)",
          boxShadow:
            "inset 0 0 0 1px rgba(255,255,255,0.04), inset 0 2px 6px rgba(0,0,0,0.6)",
        }}
      />
      <div
        className="absolute rounded-[58px] pointer-events-none"
        style={{
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, transparent 4%, transparent 96%, rgba(255,255,255,0.05) 100%)",
        }}
      />

      {/* side buttons */}
      <div className="absolute" style={{ right: -2, top: 200, width: 4, height: 92, borderRadius: "0 2px 2px 0", background: "linear-gradient(90deg, #2a2a2c 0%, #3a3a3c 50%, #1a1a1c 100%)" }} />
      <div className="absolute" style={{ left: -2, top: 170, width: 4, height: 50, borderRadius: "2px 0 0 2px", background: "linear-gradient(270deg, #2a2a2c 0%, #3a3a3c 50%, #1a1a1c 100%)" }} />
      <div className="absolute" style={{ left: -2, top: 232, width: 4, height: 50, borderRadius: "2px 0 0 2px", background: "linear-gradient(270deg, #2a2a2c 0%, #3a3a3c 50%, #1a1a1c 100%)" }} />
      <div className="absolute" style={{ left: -2, top: 120, width: 4, height: 30, borderRadius: "2px 0 0 2px", background: "linear-gradient(270deg, #2a2a2c 0%, #3a3a3c 50%, #1a1a1c 100%)" }} />

      {/* screen */}
      <div
        className="absolute overflow-hidden"
        style={{
          inset: 10,
          borderRadius: 48,
          background: "#000",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ----------------------------- Status Bar ----------------------------- */
function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 5000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function formatClock(d) {
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  h = h % 12 || 12;
  return `${h}:${m}`;
}

function StatusBar({ light = true }) {
  const now = useClock();
  const color = light ? "white" : "black";
  return (
    <div
      className="absolute inset-x-0 flex justify-between items-center pointer-events-none"
      style={{
        top: 20,
        paddingLeft: 32,
        paddingRight: 32,
        color,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: "-0.02em",
        zIndex: 55,
      }}
    >
      <span>{formatClock(now)}</span>
      <span style={{ width: 122 }} />
      <span className="flex items-center gap-1.5">
        <span className="flex items-end gap-0.5">
          <span style={{ width: 3, height: 4, background: color, borderRadius: 1 }} />
          <span style={{ width: 3, height: 6, background: color, borderRadius: 1 }} />
          <span style={{ width: 3, height: 8, background: color, borderRadius: 1 }} />
          <span style={{ width: 3, height: 10, background: color, borderRadius: 1 }} />
        </span>
        <svg width="16" height="11" viewBox="0 0 16 11" fill={color}>
          <path d="M8 10.5a1.2 1.2 0 100-2.4 1.2 1.2 0 000 2.4zM2.5 5.5a8 8 0 0111 0l-1.2 1.2a6.3 6.3 0 00-8.6 0L2.5 5.5zM.4 3.4a11 11 0 0115.2 0l-1.2 1.2a9.3 9.3 0 00-12.8 0L.4 3.4z" />
        </svg>
        <span
          className="relative"
          style={{
            width: 24,
            height: 11,
            border: `1px solid ${light ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)"}`,
            borderRadius: 3,
            padding: 1,
          }}
        >
          <span className="block" style={{ width: "85%", height: "100%", background: color, borderRadius: 1 }} />
          <span className="absolute" style={{ right: -3, top: 3, width: 2, height: 3, background: light ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)", borderRadius: "0 1px 1px 0" }} />
        </span>
      </span>
    </div>
  );
}

/* ----------------------------- Dynamic Island ----------------------------- */
function DynamicIsland() {
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2"
      style={{
        top: 14,
        width: 122,
        height: 36,
        background: "#000",
        borderRadius: 22,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04), 0 0 20px rgba(0,0,0,0.6)",
        zIndex: 56,
      }}
    >
      <div
        className="absolute"
        style={{
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "radial-gradient(circle at 30% 30%, #1a1a2e 0%, #050510 40%, #000 70%)",
          boxShadow: "inset 0 0 2px rgba(80,80,160,0.4)",
        }}
      >
        <div
          className="absolute"
          style={{
            top: 3,
            left: 3,
            width: 3,
            height: 3,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.6), transparent)",
          }}
        />
      </div>
      <div
        className="absolute"
        style={{
          left: 16,
          top: "50%",
          transform: "translateY(-50%)",
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "#0a0a14",
          boxShadow: "inset 0 0 2px rgba(40,40,80,0.6)",
        }}
      />
    </div>
  );
}

/* ----------------------------- Lock Screen ----------------------------- */
function LockScreen({ onUnlockSwipe }) {
  const y = useMotionValue(0);
  const opacity = useTransform(y, [-300, -50, 0], [0, 0.6, 1]);
  const indicatorY = useTransform(y, [-300, 0], [-20, 0]);
  const arrowOpacity = useTransform(y, [-200, -50, 0], [0, 0.4, 1]);
  const now = useClock();
  const dateStr = now.toLocaleDateString("default", { weekday: "long", month: "long", day: "numeric" });

  return (
    <motion.div
      className="absolute inset-0"
      style={{ y, opacity }}
      drag="y"
      dragConstraints={{ top: -780, bottom: 0 }}
      dragElastic={0.2}
      onDragEnd={(_, info) => {
        if (info.offset.y < -100 || info.velocity.y < -300) {
          onUnlockSwipe();
        }
      }}
    >
      {/* Wallpaper */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, #4338ca 0%, #1e1b4b 35%, #0a0a14 70%, #000 100%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse at 80% 90%, #ec4899 0%, transparent 50%)",
        }}
      />

      {/* Time + Date */}
      <div className="absolute inset-x-0 flex flex-col items-center" style={{ top: 100 }}>
        <div
          className="text-white"
          style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
            fontSize: 22,
            fontWeight: 500,
            letterSpacing: 0.5,
            textShadow: "0 2px 8px rgba(0,0,0,0.4)",
          }}
        >
          {dateStr}
        </div>
        <div
          className="text-white"
          style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
            fontSize: 92,
            fontWeight: 200,
            letterSpacing: -3,
            lineHeight: 1,
            marginTop: 4,
            textShadow: "0 4px 16px rgba(0,0,0,0.35)",
          }}
        >
          {formatClock(now)}
        </div>
      </div>

      {/* Bottom flashlight + camera */}
      <div className="absolute inset-x-0 flex justify-between" style={{ bottom: 100, paddingLeft: 32, paddingRight: 32 }}>
        {[0, 1].map((i) => (
          <div
            key={i}
            className="flex items-center justify-center"
            style={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.18)",
              backdropFilter: "blur(20px)",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12), 0 4px 20px rgba(0,0,0,0.3)",
            }}
          >
            {i === 0 ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M9 2h6l1 4-1 2v3l-2 11h-2L9 11V8L8 6l1-4z" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M9 4l-1 2H4v14h16V6h-4l-1-2H9zm3 4a5 5 0 110 10 5 5 0 010-10z" />
              </svg>
            )}
          </div>
        ))}
      </div>

      {/* swipe up indicator */}
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10"
        style={{ bottom: 30, y: indicatorY, opacity: arrowOpacity }}
      >
        <motion.div
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="text-white/80"
          style={{
            fontFamily: "-apple-system, sans-serif",
            fontSize: 13,
            fontWeight: 400,
          }}
        >
          swipe up to unlock
        </motion.div>
        <div
          style={{
            width: 134,
            height: 5,
            background: "rgba(255,255,255,0.9)",
            borderRadius: 3,
          }}
        />
      </motion.div>

      {/* glass reflection */}
      <div
        className="absolute pointer-events-none"
        style={{
          inset: 0,
          background: "linear-gradient(125deg, rgba(255,255,255,0.06) 0%, transparent 18%, transparent 82%, rgba(255,255,255,0.03) 100%)",
        }}
      />
    </motion.div>
  );
}

/* ----------------------------- Passcode Screen ----------------------------- */
function PasscodeScreen({ onSuccess }) {
  const [code, setCode] = useState("");
  const [shake, setShake] = useState(false);

  const press = (n) => {
    if (code.length >= 4) return;
    const next = code + n;
    setCode(next);
    if (next.length === 4) {
      setTimeout(() => {
        if (next === PASSCODE) {
          onSuccess();
        } else {
          setShake(true);
          setTimeout(() => {
            setShake(false);
            setCode("");
          }, 500);
        }
      }, 150);
    }
  };

  const del = () => setCode((c) => c.slice(0, -1));

  const keys = [
    { n: "1", letters: "" },
    { n: "2", letters: "ABC" },
    { n: "3", letters: "DEF" },
    { n: "4", letters: "GHI" },
    { n: "5", letters: "JKL" },
    { n: "6", letters: "MNO" },
    { n: "7", letters: "PQRS" },
    { n: "8", letters: "TUV" },
    { n: "9", letters: "WXYZ" },
  ];

  return (
    <motion.div
      className="absolute inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* blurred wallpaper */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, #4338ca 0%, #1e1b4b 35%, #0a0a14 70%, #000 100%)",
          filter: "blur(40px) brightness(0.7)",
          transform: "scale(1.1)",
        }}
      />
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(30px)" }} />

      {/* lock icon */}
      <div className="absolute inset-x-0 flex flex-col items-center" style={{ top: 90 }}>
        <svg width="26" height="32" viewBox="0 0 26 32" fill="white" style={{ opacity: 0.95 }}>
          <path d="M13 0a8 8 0 00-8 8v4H3a2 2 0 00-2 2v16a2 2 0 002 2h20a2 2 0 002-2V14a2 2 0 00-2-2h-2V8a8 8 0 00-8-8zm0 3a5 5 0 015 5v4H8V8a5 5 0 015-5z" />
        </svg>
        <div
          className="text-white"
          style={{
            fontFamily: "-apple-system, sans-serif",
            fontSize: 17,
            fontWeight: 500,
            marginTop: 14,
          }}
        >
          Enter Passcode
        </div>
        <div
          className="text-white/70"
          style={{ fontFamily: "-apple-system, sans-serif", fontSize: 12, marginTop: 4 }}
        >
          hint: 1234
        </div>

        {/* dots */}
        <motion.div
          className="flex gap-5"
          style={{ marginTop: 28 }}
          animate={shake ? { x: [-8, 8, -6, 6, -3, 3, 0] } : { x: 0 }}
          transition={{ duration: 0.45 }}
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              data-testid={`passcode-dot-${i}`}
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: "1.5px solid white",
                background: code.length > i ? "white" : "transparent",
                transition: "background 0.15s",
              }}
            />
          ))}
        </motion.div>
      </div>

      {/* keypad */}
      <div
        className="absolute inset-x-0 grid grid-cols-3"
        style={{ bottom: 80, gap: 14, paddingLeft: 30, paddingRight: 30 }}
      >
        {keys.map((k) => (
          <button
            key={k.n}
            data-testid={`key-${k.n}`}
            onClick={() => press(k.n)}
            className="flex flex-col items-center justify-center"
            style={{
              width: "100%",
              aspectRatio: "1",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.22)",
              backdropFilter: "blur(20px)",
              border: "none",
              cursor: "pointer",
              color: "white",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
              transition: "transform 0.08s, background 0.12s",
              fontFamily: "-apple-system, sans-serif",
            }}
            onPointerDown={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.35)")}
            onPointerUp={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.22)")}
            onPointerLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.22)")}
          >
            <span style={{ fontSize: 32, fontWeight: 300, lineHeight: 1 }}>{k.n}</span>
            {k.letters && (
              <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, marginTop: 2 }}>{k.letters}</span>
            )}
          </button>
        ))}
        <div />
        <button
          data-testid="key-0"
          onClick={() => press("0")}
          className="flex flex-col items-center justify-center"
          style={{
            width: "100%",
            aspectRatio: "1",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.22)",
            backdropFilter: "blur(20px)",
            border: "none",
            cursor: "pointer",
            color: "white",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
            fontFamily: "-apple-system, sans-serif",
          }}
          onPointerDown={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.35)")}
          onPointerUp={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.22)")}
          onPointerLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.22)")}
        >
          <span style={{ fontSize: 32, fontWeight: 300, lineHeight: 1 }}>0</span>
        </button>
        <button
          data-testid="key-delete"
          onClick={del}
          className="flex items-center justify-center text-white"
          style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: "-apple-system, sans-serif", fontSize: 16 }}
        >
          {code.length > 0 ? "Delete" : "Cancel"}
        </button>
      </div>
    </motion.div>
  );
}

/* ----------------------------- Home Screen ----------------------------- */
const SCREEN_W = 360; // 380 - 2*10 inset

function PageDots({ count, active, light = true }) {
  return (
    <div className="flex items-center gap-2">
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: i === active ? (light ? "white" : "black") : light ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
            transition: "background 0.25s",
          }}
        />
      ))}
    </div>
  );
}

function TodayView() {
  return (
    <div className="relative h-full w-full" style={{ paddingTop: 68 }}>
      {/* Search */}
      <div className="px-4 pb-3">
        <div
          className="flex items-center gap-2 px-3 py-2"
          style={{
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(20px)",
            borderRadius: 12,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
            <path d="M10 4a6 6 0 014.8 9.6l5.3 5.3-1.4 1.4-5.3-5.3A6 6 0 1110 4zm0 2a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
          <span style={{ color: "rgba(255,255,255,0.9)", fontFamily: "-apple-system", fontSize: 13 }}>Search</span>
        </div>
      </div>

      <div
        className="px-4 grid grid-cols-2 gap-3 overflow-hidden"
        style={{ paddingBottom: 30 }}
      >
        {/* Weather widget */}
        <div
          style={{
            gridColumn: "span 2",
            background: "linear-gradient(180deg, #4ba6ff 0%, #0066ff 100%)",
            borderRadius: 22,
            padding: 14,
            color: "white",
            fontFamily: "-apple-system",
            height: 140,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.9 }}>TORONTO</div>
          <div style={{ fontSize: 44, fontWeight: 300, lineHeight: 1, marginTop: 4 }}>72°</div>
          <div style={{ fontSize: 13, marginTop: 2 }}>Mostly Sunny</div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>H:78°  L:64°</div>
          <div style={{ position: "absolute", right: 14, top: 14, fontSize: 36 }}>
            <svg width="50" height="40" viewBox="0 0 50 40" fill="white">
              <circle cx="20" cy="14" r="10" fill="#ffd400" />
              <path d="M35 18a8 8 0 00-15-2 6 6 0 00-7 9h22a5.5 5.5 0 000-7z" />
            </svg>
          </div>
        </div>

        {/* Calendar */}
        <div
          style={{
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(20px)",
            borderRadius: 22,
            padding: 12,
            color: "white",
            fontFamily: "-apple-system",
            height: 140,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
          }}
        >
          <div style={{ fontSize: 11, color: "#ff453a", fontWeight: 600 }}>FRIDAY</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 1 }}>20</div>
          <div style={{ marginTop: 10, fontSize: 11, opacity: 0.7 }}>10:00 AM</div>
          <div style={{ fontSize: 12, fontWeight: 600 }}>Standup</div>
          <div style={{ marginTop: 6, fontSize: 11, opacity: 0.7 }}>2:30 PM</div>
          <div style={{ fontSize: 12, fontWeight: 600 }}>Pitch review</div>
        </div>

        {/* Battery */}
        <div
          style={{
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(20px)",
            borderRadius: 22,
            padding: 14,
            color: "white",
            fontFamily: "-apple-system",
            height: 140,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
          }}
        >
          <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 600, letterSpacing: 0.5 }}>BATTERIES</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
            <svg width="44" height="44" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r="18" stroke="rgba(255,255,255,0.2)" strokeWidth="4" fill="none" />
              <circle cx="22" cy="22" r="18" stroke="#30d158" strokeWidth="4" fill="none" strokeDasharray="113" strokeDashoffset="17" transform="rotate(-90 22 22)" strokeLinecap="round" />
            </svg>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>85%</div>
              <div style={{ fontSize: 10, opacity: 0.7 }}>iPhone</div>
            </div>
          </div>
        </div>

        {/* News */}
        <div
          style={{
            gridColumn: "span 2",
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(20px)",
            borderRadius: 22,
            padding: 14,
            color: "white",
            fontFamily: "-apple-system",
            height: 110,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: "#ff453a", letterSpacing: 0.5 }}>NEWS</div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8, lineHeight: 1.3 }}>
            Markets hit record highs as central banks signal pause
          </div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>Bloomberg · 2h ago</div>
        </div>
      </div>
    </div>
  );
}

function HomePage1({ apps }) {
  // legacy unused; kept to avoid import errors. AppsGrid replaces this.
  return null;
}

/* ============== Folder mini icon (preview of contents) ============== */
function FolderIcon({ items }) {
  return (
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 16,
        background: "rgba(120,120,128,0.32)",
        backdropFilter: "blur(16px)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)",
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gridTemplateRows: "repeat(3, 1fr)",
        gap: 2,
        padding: 5,
        overflow: "hidden",
      }}
    >
      {items.slice(0, 9).map((it, i) => (
        <div
          key={it.id || i}
          style={{
            transform: "scale(0.95)",
            transformOrigin: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ transform: "scale(0.23)" }}>{it.render()}</div>
        </div>
      ))}
    </div>
  );
}

/* ============== Draggable Apps Grid ============== */
function AppItem({ item, isEditing, onEnterEdit, onOpenFolder, onOpenApp, onRemove, gridId, onDragStart, onDragEnd, draggingId, registerRef }) {
  const dragControls = useDragControls();
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const itemRef = useRef(null);
  const wiggle = isEditing && draggingId !== item.id;

  const startGesture = (e) => {
    longPressFired.current = false;
    if (isEditing) {
      dragControls.start(e);
      return;
    }
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onEnterEdit();
      // Begin drag on the same pointer event
      dragControls.start(e);
    }, 500);
  };

  const cancelGesture = () => clearTimeout(longPressTimer.current);

  return (
    <motion.div
      ref={(el) => { itemRef.current = el; registerRef(item.id, el); }}
      data-testid={`item-${gridId}-${item.id}`}
      drag={isEditing}
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragSnapToOrigin
      dragElastic={1}
      onDragStart={() => onDragStart(item.id)}
      onDragEnd={(_, info) => onDragEnd(item.id, info)}
      onPointerDown={startGesture}
      onPointerUp={cancelGesture}
      onPointerCancel={cancelGesture}
      onPointerLeave={cancelGesture}
      onClick={(e) => {
        if (longPressFired.current) {
          e.preventDefault();
          longPressFired.current = false;
          return;
        }
        if (isEditing) return;
        if (item.type === "folder") {
          onOpenFolder(item, gridId);
        } else if (item.type === "app" && itemRef.current) {
          onOpenApp(item, itemRef.current.getBoundingClientRect());
        }
      }}
      animate={wiggle ? { rotate: [-1.6, 1.6, -1.6] } : { rotate: 0 }}
      transition={
        wiggle
          ? { duration: 0.24, repeat: Infinity, ease: "easeInOut" }
          : { duration: 0.15 }
      }
      whileTap={isEditing ? {} : { scale: 0.88 }}
      whileDrag={{ scale: 1.18, zIndex: 50 }}
      className="flex flex-col items-center gap-1.5 cursor-pointer relative select-none"
      style={{ zIndex: draggingId === item.id ? 50 : 1, touchAction: "none" }}
    >
      {isEditing && (
        <button
          data-testid={`remove-${item.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}
          style={{
            position: "absolute",
            top: -6,
            left: -6,
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: "rgba(80,80,84,0.95)",
            color: "white",
            border: "none",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 700,
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
            lineHeight: 1,
            paddingBottom: 2,
          }}
        >
          −
        </button>
      )}
      {item.type === "app" ? item.render() : <FolderIcon items={item.items} />}
      <span
        style={{
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
          fontSize: 11,
          color: "white",
          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
          fontWeight: 400,
          maxWidth: 70,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.label}
      </span>
    </motion.div>
  );
}

function AppsGrid({
  items,
  setItems,
  isEditing,
  onEnterEdit,
  onOpenFolder,
  onOpenApp,
  onRemove,
  gridId,
}) {
  const itemRefs = useRef({});
  const [draggingId, setDraggingId] = useState(null);

  const registerRef = (id, el) => {
    if (el) itemRefs.current[id] = el;
  };

  const onItemDragEnd = (id, info) => {
    const finalX = info.point.x;
    const finalY = info.point.y;

    let closestId = null;
    let closestDist = Infinity;
    for (const [otherId, ref] of Object.entries(itemRefs.current)) {
      if (otherId === id || !ref) continue;
      const rect = ref.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const d = Math.hypot(cx - finalX, cy - finalY);
      if (d < closestDist) {
        closestDist = d;
        closestId = otherId;
      }
    }

    setDraggingId(null);

    if (!closestId) return;

    const srcIdx = items.findIndex((i) => i.id === id);
    const tgtIdx = items.findIndex((i) => i.id === closestId);
    if (srcIdx === -1 || tgtIdx === -1 || srcIdx === tgtIdx) return;

    const target = items[tgtIdx];
    const source = items[srcIdx];

    if (target.type === "folder" && closestDist < 50) {
      const newItems = [...items];
      newItems[tgtIdx] = { ...target, items: [...target.items, source] };
      newItems.splice(srcIdx, 1);
      setItems(newItems);
      return;
    }

    if (target.type === "app" && closestDist < 30) {
      const folderName = guessFolderName(source, target);
      const newFolder = {
        id: "folder-" + Date.now(),
        type: "folder",
        label: folderName,
        items: [target, source],
      };
      const newItems = [...items];
      newItems[tgtIdx] = newFolder;
      newItems.splice(srcIdx, 1);
      setItems(newItems);
      return;
    }

    if (closestDist < 80) {
      const newItems = [...items];
      const [moved] = newItems.splice(srcIdx, 1);
      const adjustedTgt = srcIdx < tgtIdx ? tgtIdx - 1 : tgtIdx;
      newItems.splice(adjustedTgt, 0, moved);
      setItems(newItems);
    }
  };

  return (
    <div
      className="absolute inset-x-0 grid grid-cols-4"
      style={{
        top: 70,
        paddingLeft: 22,
        paddingRight: 22,
        rowGap: 18,
        columnGap: 16,
      }}
    >
      {items.map((item) => (
        <AppItem
          key={item.id}
          item={item}
          isEditing={isEditing}
          onEnterEdit={onEnterEdit}
          onOpenFolder={onOpenFolder}
          onOpenApp={onOpenApp}
          onRemove={onRemove}
          gridId={gridId}
          onDragStart={(id) => setDraggingId(id)}
          onDragEnd={onItemDragEnd}
          draggingId={draggingId}
          registerRef={registerRef}
        />
      ))}
    </div>
  );
}

function guessFolderName(a, b) {
  const la = a.label.toLowerCase();
  const lb = b.label.toLowerCase();
  if (la.startsWith("ry") && lb.startsWith("ry")) return "RY Xpress";
  // common category guesses
  const cat = (l) => {
    if (/music|podcast|tv|photo|camera|book/.test(l)) return "Media";
    if (/mail|message|facetime|phone/.test(l)) return "Social";
    if (/notes|calendar|reminder|files|translate/.test(l)) return "Productivity";
    if (/maps|weather|compass|home/.test(l)) return "Travel";
    if (/calculator|clock|settings|stocks|wallet|health|fitness/.test(l)) return "Utilities";
    return null;
  };
  const ca = cat(la);
  const cb = cat(lb);
  if (ca && cb && ca === cb) return ca;
  return "Folder";
}

function AppLibraryView({ groups }) {
  return (
    <div className="relative h-full w-full" style={{ paddingTop: 68 }}>
      <div className="px-5 pb-3">
        <div
          style={{
            color: "white",
            fontFamily: "-apple-system",
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: -0.5,
          }}
        >
          App Library
        </div>
        <div
          className="flex items-center gap-2 px-3 py-2 mt-3"
          style={{
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(20px)",
            borderRadius: 12,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.7)">
            <path d="M10 4a6 6 0 014.8 9.6l5.3 5.3-1.4 1.4-5.3-5.3A6 6 0 1110 4zm0 2a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
          <span style={{ color: "rgba(255,255,255,0.7)", fontFamily: "-apple-system", fontSize: 13 }}>App Library</span>
        </div>
      </div>

      <div className="px-5 grid grid-cols-2 gap-3">
        {groups.map((g, gi) => (
          <div
            key={gi}
            style={{
              background: "rgba(255,255,255,0.14)",
              backdropFilter: "blur(20px)",
              borderRadius: 22,
              padding: 10,
              height: 152,
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.1)",
            }}
          >
            <div
              style={{
                color: "white",
                fontFamily: "-apple-system",
                fontSize: 11,
                fontWeight: 600,
                opacity: 0.9,
                paddingLeft: 4,
                marginBottom: 6,
              }}
            >
              {g.title}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {g.items.slice(0, 4).map((it, i) => (
                <div key={i} className="flex items-center justify-center" style={{ transform: "scale(0.62)", transformOrigin: "center" }}>
                  {it}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============== Folder Open Overlay ============== */
function FolderOverlay({ folder, onClose, onRename, onRemoveItem, onOpenApp, isEditing }) {
  const [name, setName] = useState(folder.label);
  useEffect(() => setName(folder.label), [folder.label]);
  return (
    <motion.div
      className="absolute inset-0 z-40"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      data-testid="folder-overlay"
    >
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(28px)",
        }}
      />
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.6, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
        className="absolute"
        style={{ top: 90, width: 300, left: "50%", marginLeft: -150 }}
      >
        <input
          data-testid="folder-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => onRename(folder.id, name.trim() || "Folder")}
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            color: "white",
            textAlign: "center",
            fontFamily: "-apple-system",
            fontSize: 22,
            fontWeight: 600,
            width: "100%",
            marginBottom: 18,
            letterSpacing: -0.3,
          }}
        />
        <div
          style={{
            background: "rgba(40,40,45,0.6)",
            backdropFilter: "blur(40px)",
            borderRadius: 28,
            padding: 22,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
          }}
        >
          <div
            className="grid grid-cols-3"
            style={{ rowGap: 18, columnGap: 16, minHeight: 100 }}
          >
            {folder.items.map((it) => (
              <button
                key={it.id}
                data-testid={`folder-item-${it.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isEditing) return;
                  if (it.type === "app") onOpenApp && onOpenApp(it, e.currentTarget.getBoundingClientRect());
                }}
                style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                className="flex flex-col items-center gap-1.5 relative"
              >
                {isEditing && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(folder.id, it.id);
                    }}
                    style={{
                      position: "absolute",
                      top: -6,
                      left: -6,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "rgba(80,80,84,0.95)",
                      color: "white",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 700,
                      zIndex: 5,
                      lineHeight: 1,
                    }}
                  >
                    −
                  </button>
                )}
                {it.type === "app" ? it.render() : <FolderIcon items={it.items} />}
                <span
                  style={{
                    fontFamily: "-apple-system",
                    fontSize: 11,
                    color: "white",
                    maxWidth: 70,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {it.label}
                </span>
              </button>
            ))}
          </div>
          <div className="flex justify-center mt-4 gap-1.5">
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "white" }} />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ============== App Launch (icon → fullscreen morph) ============== */
function AppLaunch({ app, iconRect, screenRect, onClose }) {
  // Relative positions inside the phone screen
  const relLeft = iconRect.left - screenRect.left;
  const relTop = iconRect.top - screenRect.top;
  const iconW = iconRect.width;
  const iconH = iconRect.height;

  return (
    <motion.div
      data-testid={`app-launch-${app.id}`}
      initial={{
        left: relLeft,
        top: relTop,
        width: iconW,
        height: iconH,
        borderRadius: 16,
        opacity: 0,
      }}
      animate={{
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        borderRadius: 0,
        opacity: 1,
      }}
      exit={{
        left: relLeft,
        top: relTop,
        width: iconW,
        height: iconH,
        borderRadius: 16,
        opacity: 0,
      }}
      transition={{ duration: 0.42, ease: [0.32, 0.72, 0, 1] }}
      style={{
        position: "absolute",
        overflow: "hidden",
        zIndex: 45,
        background: "#000",
        boxShadow: "0 0 80px rgba(0,0,0,0.6)",
      }}
    >
      {/* tiny icon fade-out for the very first moment */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#000",
        }}
      >
        <div style={{ transform: "scale(1.0)" }}>{app.render && app.render()}</div>
      </motion.div>

      {/* actual app content fades in */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.18 }}
        style={{ position: "absolute", inset: 0, overflow: "hidden" }}
      >
        {getAppScreen(app.id, app.label)}
      </motion.div>

      {/* swipe-up handle to close */}
      <motion.div
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.4}
        onDragEnd={(_, info) => {
          if (info.offset.y < -60 || info.velocity.y < -300) onClose();
        }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 24,
          zIndex: 60,
          touchAction: "none",
        }}
      />
    </motion.div>
  );
}

function HomeScreen({ onLock, autoLaunch }) {
  // ---- Item factory ----
  const mk = (id, label, render) => ({ id, type: "app", label, render });
  const screenRef = useRef(null);
  const [openApp, setOpenApp] = useState(null); // { item, iconRect, screenRect }

  // Listen for global close events from RY Web overlay (back to iPhone)
  useEffect(() => {
    const handler = () => setOpenApp(null);
    window.addEventListener("ry-web-close", handler);
    return () => window.removeEventListener("ry-web-close", handler);
  }, []);

  const onOpenApp = (item, iconRect) => {
    const screenRect = screenRef.current ? screenRef.current.getBoundingClientRect() : { left: 0, top: 0, width: 360, height: 760 };
    setOpenApp({ item, iconRect, screenRect });
  };

  const initialApps1 = [
    mk("rydriver", "RY Driver", () => <RYDriver />),
    mk("ryship", "RY Ship", () => <RYShip />),
    mk("rycommand", "RY Command", () => <RYCommand />),
    mk("ryweb", "RY Web", () => <RYWeb />),
    mk("facetime", "FaceTime", () => <FaceTime />),
    mk("calendar", "Calendar", () => <Calendar />),
    mk("photos", "Photos", () => <Photos />),
    mk("camera", "Camera", () => <Camera />),
    mk("mail", "Mail", () => <Mail />),
    mk("notes", "Notes", () => <Notes />),
    mk("clock", "Clock", () => <Clock />),
    mk("maps", "Maps", () => <Maps />),
    mk("weather", "Weather", () => <Weather />),
    mk("wallet", "Wallet", () => <Wallet />),
    mk("settings", "Settings", () => <Settings />),
    mk("appstore", "App Store", () => <AppStore />),
    mk("calculator", "Calculator", () => <Calculator />),
    mk("reminders", "Reminders", () => <Notes2 />),
    mk("books", "Books", () => <Mail />),
    mk("stocks", "Stocks", () => <Weather />),
    mk("music2", "Music", () => <Music />),
  ];

  const initialApps2 = [
    mk("findmy", "Find My", () => <Photos />),
    mk("voicememos", "Voice Memos", () => <Camera />),
    mk("home", "Home", () => <Maps />),
    mk("tips", "Tips", () => <Notes2 />),
    mk("health", "Health", () => <Calendar />),
    mk("translate", "Translate", () => <Mail />),
    mk("podcasts", "Podcasts", () => <Music />),
    mk("shortcuts", "Shortcuts", () => <Calculator />),
    mk("compass", "Compass", () => <Maps />),
    mk("files", "Files", () => <Settings />),
    mk("magnifier", "Magnifier", () => <Photos />),
    mk("tv", "TV", () => <Weather />),
    mk("sleep", "Sleep", () => <Clock />),
    mk("watch", "Watch", () => <FaceTime />),
    mk("fitness", "Fitness", () => <Wallet />),
  ];

  const [items1, setItems1] = useState(initialApps1);
  const [items2, setItems2] = useState(initialApps2);
  const [isEditing, setIsEditing] = useState(false);
  const [openFolder, setOpenFolder] = useState(null); // { folder, gridId }

  // Auto-launch a specific app when arriving via direct link (/driver, /ship, /command, /web)
  useEffect(() => {
    if (!autoLaunch) return;
    const target = [...initialApps1, ...initialApps2].find((a) => a.id === autoLaunch);
    if (target) {
      const t = setTimeout(() => onOpenApp(target, { left: 180, top: 380, width: 60, height: 60 }), 50);
      return () => clearTimeout(t);
    }
     
  }, [autoLaunch]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = [
    { title: "RY Xpress", items: [<RYDriver key="a"/>, <RYShip key="b"/>, <RYCommand key="c"/>] },
    { title: "Productivity & Finance", items: [<Mail key="a"/>, <Notes key="b"/>, <Calendar key="c"/>, <Wallet key="d"/>] },
    { title: "Social", items: [<Messages key="a"/>, <FaceTime key="b"/>, <Phone key="c"/>, <Mail key="d"/>] },
    { title: "Utilities", items: [<Calculator key="a"/>, <Clock key="b"/>, <Settings key="c"/>, <Weather key="d"/>] },
    { title: "Entertainment", items: [<Music key="a"/>, <Photos key="b"/>, <Camera key="c"/>, <AppStore key="d"/>] },
    { title: "Travel", items: [<Maps key="a"/>, <Weather key="b"/>, <Wallet key="c"/>, <Safari key="d"/>] },
  ];

  const dock = [
    { c: <Phone />, label: "Phone" },
    { c: <Safari />, label: "Safari" },
    { c: <Messages />, label: "Messages" },
    { c: <Music />, label: "Music" },
  ];

  // 0 = Today View, 1 = Home1, 2 = Home2, 3 = App Library
  const [page, setPage] = useState(1);
  const x = useMotionValue(-SCREEN_W);

  const goTo = (p) => setPage(p);

  const onDragEnd = (_, info) => {
    if (isEditing) return;
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    let next = page;
    if (offset < -60 || velocity < -300) next = Math.min(3, page + 1);
    else if (offset > 60 || velocity > 300) next = Math.max(0, page - 1);
    goTo(next);
  };

  const showDock = page === 1 || page === 2;
  const dotsActive = page === 1 ? 0 : page === 2 ? 1 : -1;

  // ---- Item operations ----
  const removeFromGrid = (gridId, id) => {
    if (gridId === 1) setItems1((arr) => arr.filter((it) => it.id !== id));
    if (gridId === 2) setItems2((arr) => arr.filter((it) => it.id !== id));
  };

  const renameFolder = (folderId, newLabel) => {
    const upd = (arr) =>
      arr.map((it) => (it.id === folderId ? { ...it, label: newLabel } : it));
    setItems1(upd);
    setItems2(upd);
    setOpenFolder((f) => (f ? { ...f, folder: { ...f.folder, label: newLabel } } : null));
  };

  const removeItemFromFolder = (folderId, itemId) => {
    const upd = (arr) =>
      arr
        .map((it) => {
          if (it.id !== folderId) return it;
          const newFolderItems = it.items.filter((c) => c.id !== itemId);
          if (newFolderItems.length === 0) return null;
          if (newFolderItems.length === 1) return newFolderItems[0]; // collapse
          return { ...it, items: newFolderItems };
        })
        .filter(Boolean);
    setItems1(upd);
    setItems2(upd);
    setOpenFolder((f) => {
      if (!f || f.folder.id !== folderId) return f;
      const newItems = f.folder.items.filter((c) => c.id !== itemId);
      if (newItems.length <= 1) return null;
      return { ...f, folder: { ...f.folder, items: newItems } };
    });
  };

  return (
    <motion.div
      ref={screenRef}
      className="absolute inset-0 overflow-hidden"
      initial={{ scale: 1.15, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
    >
      {/* wallpaper */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #ff9c5b 0%, #f76b6b 30%, #b14ed8 60%, #4338ca 100%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(ellipse at 70% 10%, rgba(255,210,140,0.6) 0%, transparent 50%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse at 20% 90%, rgba(80,40,180,0.6) 0%, transparent 50%)",
        }}
      />

      {/* Pages container */}
      <motion.div
        className="absolute inset-0 flex"
        style={{ x, width: SCREEN_W * 4 }}
        drag={isEditing ? false : "x"}
        dragConstraints={{ left: -SCREEN_W * 3, right: 0 }}
        dragElastic={0.12}
        animate={{ x: -page * SCREEN_W }}
        transition={{ type: "spring", stiffness: 320, damping: 36 }}
        onDragEnd={onDragEnd}
        data-testid="home-pages"
      >
        <div style={{ width: SCREEN_W, flexShrink: 0 }} data-testid="page-today">
          <TodayView />
        </div>
        <div
          style={{ width: SCREEN_W, flexShrink: 0, position: "relative", height: "100%" }}
          data-testid="page-home1"
        >
          <AppsGrid
            items={items1}
            setItems={setItems1}
            isEditing={isEditing}
            onEnterEdit={() => setIsEditing(true)}
            onOpenFolder={(folder, gid) => setOpenFolder({ folder, gridId: gid })}
            onOpenApp={onOpenApp}
            onRemove={(id) => removeFromGrid(1, id)}
            gridId={1}
          />
        </div>
        <div
          style={{ width: SCREEN_W, flexShrink: 0, position: "relative", height: "100%" }}
          data-testid="page-home2"
        >
          <AppsGrid
            items={items2}
            setItems={setItems2}
            isEditing={isEditing}
            onEnterEdit={() => setIsEditing(true)}
            onOpenFolder={(folder, gid) => setOpenFolder({ folder, gridId: gid })}
            onOpenApp={onOpenApp}
            onRemove={(id) => removeFromGrid(2, id)}
            gridId={2}
          />
        </div>
        <div style={{ width: SCREEN_W, flexShrink: 0 }} data-testid="page-library">
          <AppLibraryView groups={groups} />
        </div>
      </motion.div>

      {/* Edit mode "Done" button */}
      <AnimatePresence>
        {isEditing && (
          <motion.button
            data-testid="edit-done"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onClick={() => setIsEditing(false)}
            className="absolute z-40"
            style={{
              top: 20,
              right: 22,
              background: "rgba(255,255,255,0.22)",
              backdropFilter: "blur(20px)",
              border: "none",
              borderRadius: 999,
              padding: "6px 14px",
              color: "white",
              fontFamily: "-apple-system",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)",
            }}
          >
            Done
          </motion.button>
        )}
      </AnimatePresence>

      {/* Search pill (only on home pages, not in edit mode) */}
      {showDock && !isEditing && (
        <button
          onClick={() => goTo(0)}
          data-testid="search-pill"
          className="absolute left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-4 py-1.5"
          style={{
            bottom: 130,
            background: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(20px)",
            borderRadius: 999,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
            border: "none",
            cursor: "pointer",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
            <path d="M10 4a6 6 0 014.8 9.6l5.3 5.3-1.4 1.4-5.3-5.3A6 6 0 1110 4zm0 2a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
          <span style={{ color: "white", fontFamily: "-apple-system", fontSize: 13 }}>Search</span>
        </button>
      )}

      {/* Page dots */}
      <div
        className="absolute left-1/2 -translate-x-1/2 z-20"
        style={{ bottom: 122 }}
      >
        {showDock && <PageDots count={2} active={dotsActive} />}
      </div>

      {/* Dock (only on home pages) */}
      {showDock && (
        <div
          className="absolute inset-x-0 mx-4 flex items-center justify-around z-20"
          style={{
            bottom: 26,
            height: 88,
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(40px)",
            borderRadius: 32,
            paddingLeft: 16,
            paddingRight: 16,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          {dock.map((a, i) => (
            <div key={i} data-testid={`dock-${a.label.toLowerCase()}`}>{a.c}</div>
          ))}
        </div>
      )}

      {/* Folder overlay */}
      <AnimatePresence>
        {openFolder && (
          <FolderOverlay
            folder={openFolder.folder}
            onClose={() => setOpenFolder(null)}
            onRename={renameFolder}
            onRemoveItem={removeItemFromFolder}
            onOpenApp={(item, rect) => {
              setOpenFolder(null);
              onOpenApp(item, rect);
            }}
            isEditing={isEditing}
          />
        )}
      </AnimatePresence>

      {/* App launch — icon morphs to fullscreen */}
      <AnimatePresence>
        {openApp && (
          <AppLaunch
            key={openApp.item.id + "-launch"}
            app={openApp.item}
            iconRect={openApp.iconRect}
            screenRect={openApp.screenRect}
            onClose={() => setOpenApp(null)}
          />
        )}
      </AnimatePresence>

      {/* home indicator — tap to close app / lock */}
      <button
        onClick={() => {
          if (openApp) return setOpenApp(null);
          if (openFolder) return setOpenFolder(null);
          if (isEditing) return setIsEditing(false);
          onLock();
        }}
        data-testid="home-indicator"
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: 10,
          width: 134,
          height: 5,
          background: openApp ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.9)",
          borderRadius: 3,
          border: "none",
          cursor: "pointer",
          padding: 0,
          zIndex: 60,
        }}
        aria-label="Lock"
      />

      {/* glass reflection */}
      <div
        className="absolute pointer-events-none z-30"
        style={{
          inset: 0,
          background: "linear-gradient(125deg, rgba(255,255,255,0.06) 0%, transparent 18%, transparent 82%, rgba(255,255,255,0.03) 100%)",
        }}
      />
    </motion.div>
  );
}

/* ----------------------------- App ----------------------------- */
function App() {
  // URL-based deep links:
  //   /driver, /ship, /command  → boot iPhone directly into that app (skip lock + home)
  //   /web                       → boot directly into RY Web macOS desktop
  //   /onepager or /proposal     → render the 1-pager pitch document (no iPhone)
  //   /                          → normal lock-screen demo
  const path = typeof window !== "undefined" ? window.location.pathname.replace(/\/$/, "") : "";
  const deepLink = {
    "/driver": "rydriver",
    "/ship": "ryship",
    "/command": "rycommand",
    "/web": "ryweb",
  }[path];
  const isOnePager = path === "/onepager" || path === "/proposal" || path === "/pitch";

  const [screen, setScreen] = useState(deepLink ? "home" : "lock");

  if (isOnePager) return <OnePager />;

  return (
    <div
      className="App min-h-screen flex items-center justify-center"
      data-testid="app-root"
      style={{
        background:
          "radial-gradient(ellipse at center, #1a1a1d 0%, #0a0a0c 60%, #050506 100%)",
        padding: 24,
      }}
    >
      <PhoneFrame>
        <StatusBar light />
        <DynamicIsland />

        <AnimatePresence mode="wait">
          {screen === "lock" && (
            <LockScreen
              key="lock"
              onUnlockSwipe={() => setScreen("passcode")}
            />
          )}
          {screen === "passcode" && (
            <PasscodeScreen key="passcode" onSuccess={() => setScreen("home")} />
          )}
          {screen === "home" && <HomeScreen key="home" onLock={() => setScreen("lock")} autoLaunch={deepLink} />}
        </AnimatePresence>
      </PhoneFrame>
    </div>
  );
}

export default App;
